import React, { useEffect, useRef } from 'react';
import { MensajeContainer } from './MensajeContainer';
import FileUploader from '../../../../components/FileUploader';
import AudioRecorder from '../utilsWhatsapp/AudioRecorder';
import { Mic, SendHorizontal, LoaderCircle, X , Paperclip } from 'lucide-react';
import { Avatar } from '../../../../utils/avatar';

const ChatComponent = ({
  chat,
  mensajes,
  loadingMensajes,
  nuevoMensaje,
  archivosEnvio,
  isRecording,
  isSending,
  onEnviarMensaje,
  onEnviarAudio,
  onNuevoMensajeChange,
  onArchivosEnvioChange,
  onStartRecording,
  onCancelRecording,
  onAbrirImagen,
  onAbrirVideo,
  onClose,
}) => {
  const finDeMensajesRef = useRef(null);
  const textareaRef = useRef(null); // <-- Ref para el textarea
  const fileInputRef = useRef(null); // <-- Ref para el input de archivo oculto

  const scrollToBottom = () => {
    finDeMensajesRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [mensajes]);

  //altura de textarea
  useEffect(() => {
    if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'; // Resetea la altura
        const scrollHeight = textareaRef.current.scrollHeight;
        textareaRef.current.style.height = `${scrollHeight}px`; // Ajusta a la altura del contenido
    }
  }, [nuevoMensaje]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Usamos la misma prop que usaba FileUploader
      onArchivosEnvioChange({ archivo: file });
    }
    // Reseteamos el input para poder seleccionar el mismo archivo de nuevo
    event.target.value = null; 
  };

  const handleAttachmentClick = () => {
    fileInputRef.current.click();
  };

  if (!chat) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
        <div className="text-center">
          <p className="text-lg mb-2">Selecciona un chat para comenzar</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-white">
      
      {/* Cabecera del Chat (con el botón de cerrar funcional) */}
      <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center min-w-0">
            <div className="flex-shrink-0">
              <Avatar name={chat.name} />
            </div>
            <div className="ml-3 flex-grow">
              <h3 className="text-lg font-semibold truncate">{chat.name}</h3>
              <p className="text-sm text-gray-500">{chat.phone_number}</p>
            </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200 flex-shrink-0">
            <X size={20} className="text-gray-600"/>
        </button>
      </div>
      
      {/* Contenedor de Mensajes */}
      <div className="flex-grow p-4 md:p-6 overflow-y-auto bg-gray-100">
        {loadingMensajes ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-blue-500 border-r-transparent"></div>
              <p className="mt-2 text-gray-500 text-sm">Cargando mensajes...</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-y-3">
            {mensajes && mensajes.length > 0 ? (
              mensajes.map(msg => (
                <MensajeContainer key={msg.id} mensaje={msg} onAbrirImagen={onAbrirImagen} onAbrirVideo={onAbrirVideo} />
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p>No hay mensajes en esta conversación.</p>
              </div>
            )}
            <div ref={finDeMensajesRef} />
          </div>
        )}
      </div>

      {/* 👇👇 ÁREA DE MENSAJES TOTALMENTE REDISEÑADA 👇👇 */}
      <div className="p-3 bg-gray-100 border-t border-gray-200 flex-shrink-0">
        
        {/* Previsualización del archivo a enviar */}
        {archivosEnvio.archivo && (
          <div className="px-3 py-2 mb-2 bg-sky-100 border border-sky-300 text-sky-800 rounded-lg flex items-center justify-between text-sm">
            <span>Archivo listo para enviar: <strong>{archivosEnvio.archivo.name}</strong></span>
            <button onClick={() => onArchivosEnvioChange({ archivo: null })} className="p-1 rounded-full hover:bg-sky-200">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Barra de input unificada */}
        <div className="w-full flex items-end gap-2 p-2 bg-white rounded-xl border border-gray-300 focus-within:ring-2 focus-within:ring-sky-500 focus-within:border-sky-500 transition-all">
          
          {/* Botón de Adjuntar Archivo */}
          <button onClick={handleAttachmentClick} className="p-2 text-gray-500 hover:text-sky-600 rounded-full hover:bg-gray-100 flex-shrink-0">
            <Paperclip size={20} />
          </button>
          {/* Input de archivo real, pero oculto */}
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileChange} 
            className="hidden"
          />

          {isRecording ? (
            <div className="w-full flex-grow">
              <AudioRecorder onSend={onEnviarAudio} onCancel={onCancelRecording} />
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              placeholder="Escribe un mensaje..."
              className="w-full p-0 bg-transparent border-none focus:ring-0 resize-none overflow-y-hidden text-sm max-h-40"
              value={nuevoMensaje}
              onChange={e => onNuevoMensajeChange(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onEnviarMensaje();
                }
              }}
              disabled={isSending}
              rows="1"
            />
          )}

          {/* Botón de Enviar o Grabar */}
          <button
            onClick={() => {
              if (nuevoMensaje.trim() || archivosEnvio.archivo) { onEnviarMensaje(); } 
              else { onStartRecording(); }
            }}
            className="bg-sky-500 text-white rounded-full h-9 w-9 flex-shrink-0 flex items-center justify-center hover:bg-sky-600 transition-colors shadow-sm disabled:bg-gray-400"
            disabled={isSending || isRecording}
          >
            {isSending ? (
              <LoaderCircle className="w-5 h-5 animate-spin" />
            ) : nuevoMensaje.trim() || archivosEnvio.archivo ? (
              <SendHorizontal className="w-5 h-5" />
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

    </div>
  );
};

export default ChatComponent;